import { useCallback, useEffect, useRef, useState } from 'react';

export type CanvasTransform = {
  x: number;
  y: number;
  scale: number;
};

export const MIN_CANVAS_SCALE = 0.05;
export const MAX_CANVAS_SCALE = 1.5;
const FIT_PADDING = 48;

const clampScale = (scale: number) =>
  Math.min(MAX_CANVAS_SCALE, Math.max(MIN_CANVAS_SCALE, scale));

const INITIAL_TRANSFORM: CanvasTransform = { x: 0, y: 0, scale: 0.4 };

const toCss = ({ x, y, scale }: CanvasTransform) =>
  `translate(${x}px, ${y}px) scale(${scale})`;

/**
 * The canvas viewport math: a translate+scale applied to the artboard layer.
 * The component wires events (wheel pans, ctrl/cmd+wheel — incl. trackpad
 * pinch — zooms around the cursor, background drag pans); this hook owns the
 * transform. No library — the viewport element and the layer's natural
 * bounds are the only inputs.
 *
 * Performance: the live transform is NOT React state. Wheel/pointer events
 * fire faster than the display refreshes and the layer holds three full-page
 * iframes, so a setState per event would re-render every artboard subtree
 * per tick. Instead the transform lives in a ref, DOM writes are coalesced
 * into one requestAnimationFrame, and only the rounded zoom percentage (for
 * the pill label) is published to React — at most once per frame, and only
 * when it actually changes.
 */
export function useCanvasTransform() {
  const transformRef = useRef<CanvasTransform>(INITIAL_TRANSFORM);
  const [zoomPercent, setZoomPercent] = useState(Math.round(INITIAL_TRANSFORM.scale * 100));
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<number | null>(null);
  // Natural (unscaled) size of the artboard layer, reported by the canvas.
  const contentSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  const setContentSize = useCallback((width: number, height: number) => {
    contentSizeRef.current = { width, height };
  }, []);

  /** Flush the ref to the DOM on the next frame; repeated calls coalesce. */
  const scheduleFlush = useCallback(() => {
    if (frameRef.current != null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const layer = layerRef.current;
      if (layer) layer.style.transform = toCss(transformRef.current);
      setZoomPercent(Math.round(transformRef.current.scale * 100));
    });
  }, []);

  useEffect(
    () => () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  /** Callback ref for the layer: paints the current transform on mount. */
  const setLayerRef = useCallback((node: HTMLDivElement | null) => {
    layerRef.current = node;
    if (node) node.style.transform = toCss(transformRef.current);
  }, []);

  /**
   * Multiply the scale, keeping the point under (clientX, clientY) — WINDOW
   * coordinates — stationary; defaults to the viewport center. Relative so it
   * is safe inside native event handlers without stale-state concerns. The
   * client→viewport conversion happens here so callers never touch the ref.
   */
  const zoomBy = useCallback((factor: number, clientX?: number, clientY?: number) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    const prev = transformRef.current;
    const nextScale = clampScale(prev.scale * factor);
    if (nextScale === prev.scale) return;
    const px = clientX != null && rect ? clientX - rect.left : (rect ? rect.width / 2 : 0);
    const py = clientY != null && rect ? clientY - rect.top : (rect ? rect.height / 2 : 0);
    const ratio = nextScale / prev.scale;
    transformRef.current = {
      scale: nextScale,
      x: px - (px - prev.x) * ratio,
      y: py - (py - prev.y) * ratio,
    };
    scheduleFlush();
  }, [scheduleFlush]);

  const panBy = useCallback((dx: number, dy: number) => {
    const prev = transformRef.current;
    transformRef.current = { ...prev, x: prev.x + dx, y: prev.y + dy };
    scheduleFlush();
  }, [scheduleFlush]);

  const fitToView = useCallback(() => {
    const viewport = viewportRef.current;
    const { width, height } = contentSizeRef.current;
    if (!viewport || width === 0 || height === 0) return;
    const rect = viewport.getBoundingClientRect();
    const scale = clampScale(
      Math.min(
        (rect.width - FIT_PADDING * 2) / width,
        (rect.height - FIT_PADDING * 2) / height,
      ),
    );
    transformRef.current = {
      scale,
      x: (rect.width - width * scale) / 2,
      // Top-align tall content (full pages are much taller than the viewport).
      y: height * scale + FIT_PADDING * 2 <= rect.height
        ? (rect.height - height * scale) / 2
        : FIT_PADDING,
    };
    scheduleFlush();
  }, [scheduleFlush]);

  return {
    zoomPercent,
    viewportRef,
    setLayerRef,
    setContentSize,
    zoomBy,
    panBy,
    fitToView,
  };
}
